import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const CONTACT_STORAGE_KEY = "lolcorp_chat_contact";
const VISITOR_ID_KEY = "lolcorp_chat_visitor_id";

/**
 * Drop-in live chat widget for the LOLCorp website. Talks to the same
 * server that runs the dashboard's API (server.js) over Socket.IO — no
 * separate chat backend to deploy.
 *
 * Usage in the main site's React app:
 *
 *   import ChatWidget from "./ChatWidget";
 *   ...
 *   <ChatWidget serverUrl="https://your-agent-server.example.com" />
 *
 * Requires `socket.io-client` installed in the SITE's own project:
 *   npm install socket.io-client
 *
 * Styling is a mix of inline styles (for layout/colors, so it drops into
 * any host site without CSS conflicts) and one scoped <style> block for
 * things inline styles can't express — @keyframes animations and a custom
 * scrollbar — all rules prefixed `.lolcorp-chat-*` so they can't collide
 * with the host site's own CSS.
 *
 * Name, email, and phone are required before a chat can start — collected
 * once via a short form, then remembered in localStorage so a returning
 * visitor isn't asked again unless they clear their browser storage. The
 * server still validates these are present (see server.js's "join"
 * handler) regardless of what this form does, so it can't be bypassed by
 * calling the socket API directly.
 *
 * Disclosure note (please don't remove): the very first message a visitor
 * sees always states plainly that they're chatting with an AI assistant —
 * this comes from the server (engine/chatEngine.js), not from this
 * component, so there's nothing to configure here.
 */
export default function ChatWidget({ serverUrl }) {
  const [open, setOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const [phase, setPhase] = useState("intake"); // "intake" | "chatting" | "ended"
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const socketRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    try {
      const saved = JSON.parse(localStorage.getItem(CONTACT_STORAGE_KEY) || "null");
      if (saved?.name && saved?.email && saved?.phone) {
        setContact(saved);
      }
    } catch {
      // ignore malformed storage
    }
  }, [open]);

  function connectSocket(contactInfo) {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }

    const socket = io(serverUrl, { transports: ["websocket", "polling"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("join", { visitorId, pageUrl: window.location.href, ...contactInfo });
    });

    socket.on("history", (data) => {
      setMessages(data.messages || []);
      setPhase(data.status === "closed" ? "ended" : "chatting");
    });

    socket.on("message", (msg) => {
      setMessages((prev) => [...prev, msg]);
      setSending(false);
    });

    socket.on("chat_ended", (data) => {
      setPhase("ended");
      setMessages((prev) => [...prev, { direction: "agent", personaName: "System", body: data.message }]);
      // Clear identity/contact info now that the server has actually
      // confirmed the chat ended — not optimistically on click, so a
      // failed end_chat request can't wipe local state while the session
      // is technically still open server-side. This is what makes "Start
      // a new chat" afterward a genuinely fresh start: new visitorId, blank
      // intake form, no stale info lingering from the previous conversation.
      localStorage.removeItem(VISITOR_ID_KEY);
      localStorage.removeItem(CONTACT_STORAGE_KEY);
    });

    socket.on("error", (err) => {
      setErrorMsg(err.message);
      setSending(false);
    });

    socket.on("disconnect", () => setConnected(false));
  }

  useEffect(() => {
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  function handleStartChat(e) {
    e.preventDefault();
    const { name, email, phone } = contact;
    if (!name.trim() || !email.trim() || !phone.trim()) return;
    localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify(contact));
    setErrorMsg(null);
    setPhase("chatting");
    connectSocket(contact);
  }

  function sendMessage() {
    const text = input.trim();
    if (!text || !socketRef.current || sending || phase !== "chatting") return;
    setMessages((prev) => [...prev, { direction: "visitor", body: text, sentAt: new Date() }]);
    socketRef.current.emit("message", { text });
    setInput("");
    setSending(true);
  }

  function handleEndChat() {
    if (!socketRef.current || phase !== "chatting") return;
    setShowEndConfirm(true);
  }

  function confirmEndChat() {
    setShowEndConfirm(false);
    socketRef.current?.emit("end_chat");
  }

  function cancelEndChat() {
    setShowEndConfirm(false);
  }

  function startNewChat() {
    socketRef.current?.disconnect();
    socketRef.current = null;
    setMessages([]);
    setPhase("intake");
    setConnected(false);
    setContact({ name: "", email: "", phone: "" });
  }

  return (
    <div style={styles.root}>
      <style>{CSS}</style>

      {open && (
        <div className="lolcorp-chat-panel-enter" style={styles.panel} data-lenis-prevent>
          {showEndConfirm && (
            <div style={styles.confirmBackdrop} className="lolcorp-chat-fade-in">
              <div style={styles.confirmCard}>
                <div style={styles.confirmIcon}>
                  <EndChatIcon />
                </div>
                <p style={styles.confirmTitle}>End this chat?</p>
                <p style={styles.confirmBody}>A transcript will be emailed to you for your records.</p>
                <div style={styles.confirmActions}>
                  <button onClick={cancelEndChat} style={styles.confirmCancelBtn} className="lolcorp-chat-btn-ghost">
                    Cancel
                  </button>
                  <button onClick={confirmEndChat} style={styles.confirmEndBtn} className="lolcorp-chat-btn-danger">
                    End Chat
                  </button>
                </div>
              </div>
            </div>
          )}
          <div style={styles.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="lolcorp-chat-status-dot" />
              <span style={styles.headerTitle}>LOLCorp PVT LTD Sales Chat</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {phase === "chatting" && (
                <button onClick={handleEndChat} style={styles.endChatBtn}>
                  End Chat
                </button>
              )}
              <button onClick={() => setOpen(false)} style={styles.iconBtn} aria-label="Close chat">
                <CloseIcon />
              </button>
            </div>
          </div>

          {phase === "intake" && (
            <form onSubmit={handleStartChat} style={styles.intakeForm} className="lolcorp-chat-fade-in">
              <p style={styles.intakeIntro}>
                Quick details before we start, so we can follow up properly if needed.
              </p>
              <input
                required
                placeholder="Your name"
                value={contact.name}
                onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                className="lolcorp-chat-input"
                style={styles.intakeInput}
              />
              <input
                required
                type="email"
                placeholder="Email address"
                value={contact.email}
                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                className="lolcorp-chat-input"
                style={styles.intakeInput}
              />
              <input
                required
                type="tel"
                placeholder="Phone number"
                value={contact.phone}
                onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                className="lolcorp-chat-input"
                style={styles.intakeInput}
              />
              {errorMsg && <div style={styles.errorText}>{errorMsg}</div>}
              <button type="submit" style={styles.sendBtnFull} className="lolcorp-chat-btn">
                Start chat
              </button>
            </form>
          )}

          {phase !== "intake" && (
            <>
              <div ref={scrollRef} className="lolcorp-chat-scroll" style={styles.messages} data-lenis-prevent>
                {!connected && messages.length === 0 && (
                  <div style={styles.systemLine}>Connecting…</div>
                )}
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className="lolcorp-chat-fade-in"
                    style={{ display: "flex", gap: 8, alignItems: "flex-end", justifyContent: m.direction === "visitor" ? "flex-end" : "flex-start" }}
                  >
                    {m.direction !== "visitor" && (
                      <div style={styles.avatar}>{(m.personaName || "?")[0]}</div>
                    )}
                    <div style={m.direction === "visitor" ? styles.bubbleVisitor : styles.bubbleAgent}>
                      {m.direction !== "visitor" && <div style={styles.bubbleSender}>{m.personaName}</div>}
                      <div>{m.body}</div>
                    </div>
                  </div>
                ))}
                {sending && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={styles.avatarPulse} />
                    <div style={styles.typingBubble}>
                      <span className="lolcorp-chat-dot" />
                      <span className="lolcorp-chat-dot" />
                      <span className="lolcorp-chat-dot" />
                    </div>
                  </div>
                )}
                {errorMsg && <div style={styles.errorText}>{errorMsg}</div>}
              </div>

              {phase === "chatting" ? (
                <div style={styles.inputRow}>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Type a message…"
                    className="lolcorp-chat-input"
                    style={styles.input}
                  />
                  <button
                    onClick={sendMessage}
                    style={styles.sendIconBtn}
                    className="lolcorp-chat-btn"
                    disabled={!input.trim()}
                    aria-label="Send"
                  >
                    <SendIcon />
                  </button>
                </div>
              ) : (
                <div style={styles.inputRow}>
                  <button onClick={startNewChat} style={styles.sendBtnFull} className="lolcorp-chat-btn">
                    Start a new chat
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        style={styles.bubble}
        className="lolcorp-chat-bubble-btn"
        aria-label="Open chat"
      >
        {open ? <CloseIcon size={24} /> : <ChatIcon />}
      </button>
    </div>
  );
}

function ChatIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9l-4.5 4V17H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"
        fill="white"
      />
    </svg>
  );
}

function CloseIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M3 11l18-8-8 18-2.5-7.5L3 11z" fill="white" />
    </svg>
  );
}

function EndChatIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2a10 10 0 100 20 10 10 0 000-20zm3.5 6.5l-7 7M8.5 8.5l7 7"
        stroke="#B3261E"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

const TEAL = "#003333";
const TEAL_LIGHT = "#0A4A4A";

const CSS = `
@keyframes lolcorp-chat-panel-in {
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes lolcorp-chat-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes lolcorp-chat-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,51,51,0.35); }
  50% { box-shadow: 0 0 0 8px rgba(0,51,51,0); }
}
@keyframes lolcorp-chat-dot-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30% { transform: translateY(-4px); opacity: 1; }
}
.lolcorp-chat-panel-enter { animation: lolcorp-chat-panel-in 0.22s cubic-bezier(0.2, 0.8, 0.2, 1); }
.lolcorp-chat-fade-in { animation: lolcorp-chat-fade-in 0.25s ease-out; }
.lolcorp-chat-bubble-btn { transition: transform 0.15s ease, box-shadow 0.15s ease; animation: lolcorp-chat-pulse 2.5s ease-in-out infinite; }
.lolcorp-chat-bubble-btn:hover { transform: scale(1.08); }
.lolcorp-chat-bubble-btn:active { transform: scale(0.96); }
.lolcorp-chat-btn { transition: background 0.15s ease, transform 0.1s ease; }
.lolcorp-chat-btn:hover:not(:disabled) { background: ${TEAL_LIGHT} !important; }
.lolcorp-chat-btn:active:not(:disabled) { transform: scale(0.97); }
.lolcorp-chat-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.lolcorp-chat-btn-ghost { transition: background 0.15s ease, transform 0.1s ease; }
.lolcorp-chat-btn-ghost:hover { background: #F0F2F1 !important; }
.lolcorp-chat-btn-ghost:active { transform: scale(0.97); }
.lolcorp-chat-btn-danger { transition: background 0.15s ease, transform 0.1s ease; }
.lolcorp-chat-btn-danger:hover { background: #8F1E17 !important; }
.lolcorp-chat-btn-danger:active { transform: scale(0.97); }
.lolcorp-chat-input { transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.lolcorp-chat-input:focus { outline: none; border-color: ${TEAL} !important; box-shadow: 0 0 0 3px rgba(0,51,51,0.1); }
.lolcorp-chat-status-dot { width: 7px; height: 7px; border-radius: 50%; background: #4ADE80; display: inline-block; box-shadow: 0 0 0 2px rgba(74,222,128,0.3); }
.lolcorp-chat-dot { width: 6px; height: 6px; border-radius: 50%; background: ${TEAL}; display: inline-block; margin: 0 2px; animation: lolcorp-chat-dot-bounce 1.1s ease-in-out infinite; }
.lolcorp-chat-dot:nth-child(2) { animation-delay: 0.15s; }
.lolcorp-chat-dot:nth-child(3) { animation-delay: 0.3s; }
.lolcorp-chat-scroll { scrollbar-width: thin; scrollbar-color: #C9D4D2 transparent; }
.lolcorp-chat-scroll::-webkit-scrollbar { width: 6px; }
.lolcorp-chat-scroll::-webkit-scrollbar-track { background: transparent; }
.lolcorp-chat-scroll::-webkit-scrollbar-thumb { background: #C9D4D2; border-radius: 10px; }
.lolcorp-chat-scroll::-webkit-scrollbar-thumb:hover { background: #9FB0AD; }
`;

const styles = {
  root: { position: "fixed", bottom: 24, right: 24, zIndex: 9999, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  bubble: {
    width: 58,
    height: 58,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${TEAL}, ${TEAL_LIGHT})`,
    color: "white",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 6px 20px rgba(0,51,51,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  panel: {
    position: "absolute",
    bottom: 74,
    right: 0,
    width: 350,
    height: 490,
    background: "white",
    borderRadius: 16,
    boxShadow: "0 12px 40px rgba(0,20,20,0.25)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    background: `linear-gradient(135deg, ${TEAL}, ${TEAL_LIGHT})`,
    color: "white",
    padding: "14px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  },
  headerTitle: { fontWeight: 700, fontSize: 13.5, letterSpacing: 0.2 },
  iconBtn: {
    background: "rgba(255,255,255,0.12)",
    border: "none",
    color: "white",
    cursor: "pointer",
    lineHeight: 1,
    width: 28,
    height: 28,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  endChatBtn: {
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.35)",
    color: "white",
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 7,
    padding: "5px 10px",
    cursor: "pointer",
  },
  intakeForm: { padding: 20, display: "flex", flexDirection: "column", gap: 12, flex: 1, minHeight: 0 },
  intakeIntro: { fontSize: 13, color: "#5C6B69", margin: "0 0 4px 0", lineHeight: 1.5 },
  intakeInput: { border: "1.5px solid #E3E7E4", borderRadius: 10, padding: "11px 12px", fontSize: 14 },
  errorText: { fontSize: 12, color: "#B3261E", padding: "0 4px" },
  confirmBackdrop: {
    position: "absolute",
    inset: 0,
    background: "rgba(18,32,31,0.45)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    padding: 24,
  },
  confirmCard: {
    background: "white",
    borderRadius: 14,
    padding: "22px 20px",
    width: "100%",
    maxWidth: 260,
    boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
    textAlign: "center",
  },
  confirmIcon: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "rgba(179,38,30,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
  },
  confirmTitle: { fontSize: 15, fontWeight: 700, color: "#12201F", margin: "0 0 6px 0" },
  confirmBody: { fontSize: 13, color: "#6B7876", margin: "0 0 18px 0", lineHeight: 1.5 },
  confirmActions: { display: "flex", gap: 8 },
  confirmCancelBtn: {
    flex: 1,
    background: "#F7F8F6",
    color: "#12201F",
    border: "1.5px solid #E3E7E4",
    borderRadius: 9,
    padding: "9px 0",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
  },
  confirmEndBtn: {
    flex: 1,
    background: "#B3261E",
    color: "white",
    border: "none",
    borderRadius: 9,
    padding: "9px 0",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
  },
  // The critical fix: minHeight: 0 on a flex child with overflow-y auto.
  // Without it, browsers size this to fit all its content instead of
  // respecting the flex constraint, which is exactly what broke wheel
  // scrolling (the visible box and the actual overflow area go out of
  // sync — dragging the scrollbar still "worked" because it's tied to the
  // real dimensions, but the wheel target area wasn't matching what was
  // visible).
  messages: { flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", padding: "14px 12px", display: "flex", flexDirection: "column", gap: 10, background: "#FAFBFA" },
  systemLine: { fontSize: 12, color: "#8A9997", textAlign: "center" },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: TEAL,
    color: "white",
    fontSize: 11,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  avatarPulse: { width: 26, height: 26, borderRadius: "50%", background: "#E3E7E4", flexShrink: 0 },
  bubbleVisitor: {
    background: `linear-gradient(135deg, ${TEAL}, ${TEAL_LIGHT})`,
    color: "white",
    padding: "9px 13px",
    borderRadius: "14px 14px 3px 14px",
    maxWidth: "76%",
    fontSize: 14,
    lineHeight: 1.4,
    boxShadow: "0 2px 8px rgba(0,51,51,0.15)",
  },
  bubbleAgent: {
    background: "white",
    color: "#12201F",
    padding: "9px 13px",
    borderRadius: "14px 14px 14px 3px",
    maxWidth: "76%",
    fontSize: 14,
    lineHeight: 1.4,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    border: "1px solid #EEF1F0",
  },
  bubbleSender: { fontSize: 10.5, fontWeight: 700, marginBottom: 2, color: TEAL, textTransform: "uppercase", letterSpacing: 0.3 },
  typingBubble: {
    background: "white",
    padding: "10px 14px",
    borderRadius: "14px 14px 14px 3px",
    border: "1px solid #EEF1F0",
    display: "flex",
    alignItems: "center",
  },
  inputRow: { display: "flex", borderTop: "1px solid #EEF1F0", padding: 10, gap: 8, flexShrink: 0, background: "white" },
  input: { flex: 1, border: "1.5px solid #E3E7E4", borderRadius: 10, padding: "9px 12px", fontSize: 14 },
  sendIconBtn: {
    background: TEAL,
    border: "none",
    borderRadius: 10,
    width: 38,
    height: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  sendBtnFull: {
    background: TEAL,
    color: "white",
    border: "none",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
  },
};
